import React, { useState, useEffect } from "react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/footer";
import API from "../../api/api";
import Swal from "sweetalert2";
import { FaUser, FaEnvelope, FaPhone, FaLock, FaKey, FaShieldAlt, FaMapMarkerAlt } from "react-icons/fa";

const AdminProfile = () => {
  const role = localStorage.getItem("role");
  
  // Profile state
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Edit Profile form
  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    mobile: ""
  });
  const [updatingProfile, setUpdatingProfile] = useState(false);

  // Change Password form
  const [passForm, setPassForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
    email_otp: ""
  });
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [updatingPassword, setUpdatingPassword] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const res = await API.fetchProfile();
      if (res) {
        setProfile(res);
        setEditForm({
          name: res.name || "",
          email: res.email || "",
          mobile: res.mobile || ""
        });
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to load profile details.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  // Cooldown effect
  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    if (!editForm.name.trim() || !editForm.email.trim()) {
      Swal.fire("Warning", "Name and email are required.", "warning");
      return;
    }

    try {
      setUpdatingProfile(true);
      await API.updateProfile(editForm);
      Swal.fire("Success", "Profile updated successfully.", "success");
      loadProfile();
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err?.response?.data?.message || err?.message || "Failed to update profile.", "error");
    } finally {
      setUpdatingProfile(false);
    }
  };

  const handleSendOtp = async () => {
    try {
      setSendingOtp(true);
      await API.sendPasswordOtp();
      setOtpSent(true);
      setCooldown(60);
      Swal.fire("Success", "OTP sent successfully to your registered email.", "success");
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err?.response?.data?.message || err?.message || "Failed to send OTP.", "error");
    } finally {
      setSendingOtp(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!passForm.current_password || !passForm.new_password || !passForm.confirm_password || !passForm.email_otp) {
      Swal.fire("Warning", "All password fields and OTP are required.", "warning");
      return;
    }
    if (passForm.new_password !== passForm.confirm_password) {
      Swal.fire("Warning", "New password and confirmation do not match.", "warning");
      return;
    }

    try {
      setUpdatingPassword(true);
      await API.changePassword(passForm);
      Swal.fire("Success", "Password changed successfully. Please login again.", "success").then(() => {
        sessionStorage.clear();
        localStorage.clear();
        window.location.href = "/login";
      });
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err?.response?.data?.message || err?.message || "Failed to change password.", "error");
    } finally {
      setUpdatingPassword(false);
    }
  };

  return (
    <>
      <Navbar role={role} />
      <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto space-y-8" style={{ marginTop: "30px" }}>
          
          {/* Header Card */}
          <div className="bg-gradient-to-r from-slate-800 to-indigo-900 rounded-3xl p-6 sm:p-8 text-white shadow-xl flex flex-col sm:flex-row items-center gap-6">
            <div className="h-20 w-20 rounded-2xl bg-indigo-500/20 border border-indigo-400/30 flex items-center justify-center text-3xl font-bold uppercase shadow-inner">
              {profile?.name ? profile.name.slice(0, 2) : <FaUser className="text-2xl text-indigo-200" />}
            </div>
            <div className="text-center sm:text-left space-y-1">
              <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-indigo-500/30 border border-indigo-400/40 text-indigo-200 uppercase tracking-wider">
                {profile?.role || role || "User"} Account
              </span>
              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                {profile?.name || "Loading..."}
              </h1>
              <p className="text-sm text-slate-300">
                Manage your credentials and profile details
              </p>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12 bg-white rounded-3xl border border-slate-200 shadow-sm">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="text-slate-500 text-sm mt-4">Loading your profile info...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              
              {/* Profile Details Panel */}
              <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-md space-y-6">
                <div>
                  <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                    <FaUser className="text-indigo-600 text-sm" /> Personal Details
                  </h2>
                  <p className="text-xs text-slate-500 mt-1">
                    Your personal information is displayed here. You can update it below.
                  </p>
                </div>

                <form onSubmit={handleUpdateProfile} className="space-y-4">
                  <div>
                    <label className="block mb-1 text-xs font-semibold text-slate-600">Full Name</label>
                    <div className="relative">
                      <FaUser className="absolute left-3 top-3 text-slate-400" />
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className="w-full text-sm border border-slate-250 rounded-xl pl-9 pr-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                        placeholder="Full Name"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block mb-1 text-xs font-semibold text-slate-600">Email Address</label>
                    <div className="relative">
                      <FaEnvelope className="absolute left-3 top-3 text-slate-400" />
                      <input
                        type="email"
                        value={editForm.email}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                        className="w-full text-sm border border-slate-250 rounded-xl pl-9 pr-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                        placeholder="Email Address"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block mb-1 text-xs font-semibold text-slate-600">Mobile Number</label>
                    <div className="relative">
                      <FaPhone className="absolute left-3 top-3 text-slate-400" />
                      <input
                        type="text"
                        value={editForm.mobile}
                        onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value })}
                        className="w-full text-sm border border-slate-250 rounded-xl pl-9 pr-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                        placeholder="Mobile Number"
                      />
                    </div>
                  </div>

                  {profile?.district && (
                    <div className="pt-2">
                      <div className="p-3 bg-slate-50 rounded-xl border border-slate-150 flex items-center gap-3">
                        <FaMapMarkerAlt className="text-slate-500" />
                        <div>
                          <p className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Assigned District</p>
                          <p className="text-sm font-semibold text-slate-800">{profile.district}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={updatingProfile}
                    className="w-full py-3 px-4 rounded-xl bg-indigo-600 text-white font-bold text-sm hover:bg-indigo-700 transition-colors disabled:opacity-50"
                  >
                    {updatingProfile ? "Updating Profile..." : "Update Profile Details"}
                  </button>
                </form>
              </div>

              {/* Password Panel */}
              <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-md space-y-6">
                <div>
                  <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                    <FaLock className="text-indigo-600 text-sm" /> Security Settings
                  </h2>
                  <p className="text-xs text-slate-500 mt-1">
                    Keep your account secure by modifying your password regularly.
                  </p>
                </div>

                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div>
                    <label className="block mb-1 text-xs font-semibold text-slate-600">Current Password</label>
                    <div className="relative">
                      <FaLock className="absolute left-3 top-3 text-slate-400" />
                      <input
                        type="password"
                        value={passForm.current_password}
                        onChange={(e) => setPassForm({ ...passForm, current_password: e.target.value })}
                        className="w-full text-sm border border-slate-250 rounded-xl pl-9 pr-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                        placeholder="Current Password"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block mb-1 text-xs font-semibold text-slate-600">New Password</label>
                    <div className="relative">
                      <FaKey className="absolute left-3 top-3 text-slate-400" />
                      <input
                        type="password"
                        value={passForm.new_password}
                        onChange={(e) => setPassForm({ ...passForm, new_password: e.target.value })}
                        className="w-full text-sm border border-slate-250 rounded-xl pl-9 pr-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                        placeholder="New Password"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block mb-1 text-xs font-semibold text-slate-600">Confirm New Password</label>
                    <div className="relative">
                      <FaShieldAlt className="absolute left-3 top-3 text-slate-400" />
                      <input
                        type="password"
                        value={passForm.confirm_password}
                        onChange={(e) => setPassForm({ ...passForm, confirm_password: e.target.value })}
                        className="w-full text-sm border border-slate-250 rounded-xl pl-9 pr-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                        placeholder="Confirm Password"
                        required
                      />
                    </div>
                  </div>

                  <div className="border-t border-slate-100 pt-4 space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-slate-600">Email Verification OTP</label>
                        <p className="text-[10px] text-slate-400">Required to authorize password change</p>
                        {otpSent && <p className="text-[10px] text-emerald-600 font-semibold mt-1">✓ OTP sent to email</p>}
                      </div>
                      <button
                        type="button"
                        onClick={handleSendOtp}
                        disabled={sendingOtp || cooldown > 0}
                        className="py-1.5 px-3 rounded-lg bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs font-semibold hover:bg-indigo-100 disabled:opacity-50"
                      >
                        {cooldown > 0 ? `Resend in ${cooldown}s` : "Get OTP"}
                      </button>
                    </div>

                    <input
                      type="text"
                      maxLength={6}
                      value={passForm.email_otp}
                      onChange={(e) => setPassForm({ ...passForm, email_otp: e.target.value.replace(/\D/g, "") })}
                      className="w-full text-center text-sm tracking-widest font-mono border border-slate-250 rounded-xl py-2 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                      placeholder="XXXXXX"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={updatingPassword}
                    className="w-full py-3 px-4 rounded-xl bg-slate-900 text-white font-bold text-sm hover:bg-slate-800 transition-colors disabled:opacity-50"
                  >
                    {updatingPassword ? "Changing Password..." : "Change Password"}
                  </button>
                </form>
              </div>

            </div>
          )}

        </div>
      </div>
      <Footer />
    </>
  );
};

export default AdminProfile;
